/**
 * /home — the editorial recent-visits feed.
 *
 * Sections (in order):
 *   1. InstallPromptStrip — surfaced once when beforeinstallprompt fires.
 *   2. PageHeader — title + welcome copy.
 *   3. Go-to shortcuts — small chip row of places with status === 'go-to'.
 *   4. Recent visits feed — grouped by relative date band.
 *
 * Empty state: a single editorial empty-state with CTA to open the
 * capture sheet.
 *
 * Loading: shimmer skeletons matching the layout (NOT spinners).
 * Error: editorial banner with "Try again" link.
 */
import { Link } from 'react-router-dom';
import type { PlaceSummary } from '@shared/types';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { StatusPill } from '../components/ui/StatusPill';
import { VisitTimelineEntry } from '../components/VisitTimelineEntry';
import { InstallPromptStrip } from '../components/InstallPromptStrip';
import { useAppStore } from '../lib/store';
import { useActiveHouseholdId } from '../lib/household';
import {
  useCategories,
  usePlaces,
  useRecentVisits,
  type VisitFeedItem,
} from '../lib/queries';

export default function HomeRoute() {
  const openSheet = useAppStore((s) => s.openCaptureSheet);
  const householdId = useActiveHouseholdId();
  const recent = useRecentVisits(householdId, 50);
  const goTo = usePlaces(householdId, { status: 'go-to', limit: 24 });
  const categories = useCategories(householdId);

  if (recent.isPending || goTo.isPending) {
    return <HomeSkeleton />;
  }

  if (recent.error || goTo.error) {
    return <HomeError onRetry={() => { void recent.refetch(); void goTo.refetch(); }} />;
  }

  const visits = recent.data?.visits ?? [];
  const goToPlaces = goTo.data?.places ?? [];

  if (!visits.length && !goToPlaces.length) {
    return (
      <>
        <InstallPromptStrip />
        <PageHeader
          eyebrow="Your journal"
          title="What you keep returning to."
          description="A private journal for the places you live with. Capture an entry to begin."
        />
        <EmptyState
          title="No journal entries yet."
          description="Capture your first taste — a restaurant, a hotel, anywhere you want to remember."
          cta={
            <Button variant="primary" size="lg" onClick={openSheet}>
              Capture your first entry
            </Button>
          }
        />
      </>
    );
  }

  // Group visits by relative band: Today, Yesterday, This week, Earlier
  const grouped = groupByBand(visits);
  const categoryById = new Map(
    (categories.data?.categories ?? []).map((c) => [c.id, c]),
  );

  return (
    <>
      <InstallPromptStrip />
      <PageHeader
        eyebrow="Your journal"
        title="Recently kept."
        actions={
          <Button variant="secondary" onClick={openSheet}>
            Capture entry
          </Button>
        }
      />

      {goToPlaces.length ? (
        <section className="mb-12" aria-labelledby="goto-heading">
          <h2
            id="goto-heading"
            className="font-serif text-lg text-ink-700 mb-4"
          >
            Go-to places
          </h2>
          <div className="flex flex-wrap gap-3 -mx-1 px-1 overflow-x-auto md:overflow-visible">
            {goToPlaces.map((p) => (
              <GoToChip
                key={p.id}
                place={p}
                categoryName={categoryById.get(p.category_id)?.name ?? null}
              />
            ))}
          </div>
        </section>
      ) : null}

      {visits.length ? (
        <section aria-labelledby="recent-heading">
          <h2 id="recent-heading" className="sr-only">
            Recent journal entries
          </h2>
          {grouped.map(({ band, items }) => (
            <div key={band} className="mb-10">
              <h3 className="font-serif italic text-base text-ink-500 mb-2">
                {band}
              </h3>
              <div className="divide-y divide-ink-100">
                {items.map((v) => (
                  <VisitTimelineEntry
                    key={v.id}
                    visit={v}
                    placeName={v.place_name}
                    placeId={v.place_id}
                    dateFormat="relative"
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      ) : (
        <EmptyState
          title="No journal entries yet."
          description="Capture your first taste to begin."
          cta={
            <Button variant="primary" onClick={openSheet}>
              Capture an entry
            </Button>
          }
        />
      )}
    </>
  );
}

function GoToChip({
  place,
  categoryName,
}: {
  place: PlaceSummary;
  categoryName: string | null;
}) {
  return (
    <Link
      to={`/places/${place.id}`}
      className={
        'group inline-flex flex-col gap-1 px-4 py-3 border border-ink-200 bg-surface hover:bg-surface-2 transition-colors duration-200 ease-out-slow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent min-w-[10rem]'
      }
    >
      <span className="font-sans text-[0.65rem] uppercase tracking-[0.18em] text-ink-500">
        {categoryName ?? 'Place'}
      </span>
      <span className="font-serif text-base text-ink-900 leading-tight">
        {place.name}
      </span>
      <span className="mt-1">
        <StatusPill status={place.status} />
      </span>
    </Link>
  );
}

interface BandGroup {
  band: string;
  items: VisitFeedItem[];
}

function groupByBand(visits: VisitFeedItem[]): BandGroup[] {
  const today = new Date();
  const startOf = (d: Date) => {
    const c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c.getTime();
  };
  const todayMs = startOf(today);
  const dayMs = 24 * 60 * 60 * 1000;
  const groups = new Map<string, BandGroup>();

  for (const v of visits) {
    const t = new Date(v.visited_at).getTime();
    let key = 'Earlier';
    if (t >= todayMs) key = 'Today';
    else if (t >= todayMs - dayMs) key = 'Yesterday';
    else if (t >= todayMs - 6 * dayMs) key = 'This week';
    else if (t >= todayMs - 30 * dayMs) key = 'This month';

    let g = groups.get(key);
    if (!g) {
      g = { band: key, items: [] };
      groups.set(key, g);
    }
    g.items.push(v);
  }

  const order = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'];
  return order.flatMap((k) => (groups.has(k) ? [groups.get(k)!] : []));
}

function HomeSkeleton() {
  return (
    <div data-testid="home-skeleton">
      <div className="mb-10 pb-8 border-b border-ink-200">
        <Skeleton className="h-4 w-24 mb-3" />
        <Skeleton className="h-12 w-2/3" />
      </div>
      <section className="mb-12">
        <Skeleton className="h-5 w-32 mb-4" />
        <div className="flex gap-3">
          <Skeleton className="h-20 w-40" />
          <Skeleton className="h-20 w-40" />
          <Skeleton className="h-20 w-40" />
        </div>
      </section>
      <section>
        <Skeleton className="h-5 w-32 mb-4" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="py-6 border-b border-ink-100">
            <Skeleton className="h-3 w-24 mb-2" />
            <Skeleton className="h-4 w-1/3 mb-3" />
            <Skeleton className="h-4 w-full mb-1" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </section>
    </div>
  );
}

function HomeError({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState
      title="We couldn't load your journal."
      description="Check your connection and try again."
      cta={
        <Button variant="primary" onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}

// Re-export for tests that bypass router context.
export { groupByBand, HomeSkeleton };
